import { IonButton } from '@ionic/react';
import { PRODUCTS } from '../../data/products';
import { createCatalog, productOf } from '../../domain/price-book';
import { formatDate, formatMoney, formatPrice, productName, storeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { FoodIcon, FoodPlate } from '../pictures/FoodPlate';
import { productPicture } from '../pictures/pictures';
import { selectTab } from '../tabs';
import type { ShoppingToday } from './today-summary';

type TripToday = Extract<ShoppingToday, { kind: 'trip' }>;
type NoTripToday = Extract<ShoppingToday, { kind: 'none' }>;

// DE: Nur gezeigt, wenn es Bilder gibt; die Namen stehen als Bildtext da.
// EN: Shown only when there are pictures; the names are their text alternative.
function Strip({ productIds }: { productIds: readonly string[] }) {
  const { language, t } = useMessages();
  if (productIds.length === 0) return null;
  const catalog = createCatalog(PRODUCTS);
  return (
    <div className="app-strip">
      <span className="app-strip-label">{t.amongShopping}</span>
      <span className="app-strip-plates">
        {productIds.map((productId) => {
          const product = productOf(catalog, productId);
          return (
            <FoodPlate
              key={productId}
              picture={productPicture(product)}
              size={32}
              label={productName(product, language)}
            />
          );
        })}
      </span>
    </div>
  );
}

function TripRows({ trip }: { trip: TripToday }) {
  const { language, t } = useMessages();
  const price = (euros: number) => formatPrice(euros, language, trip.estimated);
  const total =
    trip.wineTotal > 0
      ? t.totalWithWine(price(trip.total), price(trip.wineTotal))
      : t.total(price(trip.total));
  return (
    <>
      <p className="app-lead">{trip.extra ? t.shoppingExtra : t.shoppingToday}</p>
      <ul className="app-rows">
        {trip.stores.map((row) => (
          <li key={row.storeId} className="app-row">
            <span className="app-row-name">
              <FoodIcon picture="basket" size={24} />
              {storeName(row.storeId)}
            </span>
            <span className="app-row-value">{`${t.itemCount(row.items)} · ${price(row.total)}`}</span>
          </li>
        ))}
      </ul>
      <Strip productIds={trip.strip} />
      <p className="app-muted">{total}</p>
      <IonButton expand="block" onClick={() => selectTab('shopping')}>
        {t.openShoppingList}
      </IonButton>
    </>
  );
}

function NoTrip({ none }: { none: NoTripToday }) {
  const { language, t } = useMessages();
  const next = none.nextTrip;
  return (
    <>
      <p className="app-lead">{none.closedToday ? t.shopsClosedToday : t.nothingToBuy}</p>
      {next && (
        <p className="app-muted">
          {t.nextTrip(
            formatDate(next.date, language, 'weekday-long'),
            next.storeIds.map(storeName).join(', '),
            formatMoney(next.total, language),
          )}
        </p>
      )}
    </>
  );
}

/**
 * DE: Erste Frage des Tages: Muss ich heute einkaufen, wo und für wie viel?
 * EN: First question of the day: do I have to shop today, where and for how much?
 */
export function ShoppingCard({ shopping }: { shopping: ShoppingToday }) {
  const { t } = useMessages();
  return (
    <section className="app-card">
      <h2>
        <FoodIcon picture="shopping-cart" size={20} />
        {t.shoppingTitle}
      </h2>
      {shopping.kind === 'trip' ? <TripRows trip={shopping} /> : <NoTrip none={shopping} />}
    </section>
  );
}
